import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ContainerIcon, FileTextIcon, MapPinIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import type { LocationDTO } from '@invenco/common-interface/accounts';
import { AsnStatus } from '@invenco/supply-interface';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { DataImporter } from '@/components/data-importer/data-importer';
import { TemplateKey } from '@/components/data-importer/types';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useDataTableViews,
  useRowNavigationCallback,
  type View,
} from '@/components/data-table';
import { Header, HeaderTitle, HeaderActions, HeaderIcon } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { DATA_POLLING_DURATION_MS, LIVE_DATA_INTERVAL_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { QueryAsnsProps, SortField } from '@/gateways/impl/SupplyGateway';
import { formatDate, formatNumber } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { usePulseFlag } from '@/shared/hooks/use-pulse-flag';
import { asnStatusType } from '@/shared/status-maps';
import { asnStatusTitle } from '@/shared/title-maps';

import { AsnListItem, AsnListMapper } from './asn-list-mapper';

export const POLL_REFETCH_COUNT = 5;

const columnHelper = createColumnHelper<AsnListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => <Badge variant="secondary">{getValue()}</Badge>,
  }),
  columnHelper.accessor('reference', {
    header: 'Reference',
    meta: { truncate: true },
  }),
  columnHelper.accessor('expectedAt', {
    header: 'Date Expected',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value) : undefined;
    },
  }),
  columnHelper.accessor('locationName', {
    header: 'Location',
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? (
        <StatusBadge status={status} type={asnStatusType} title={asnStatusTitle} />
      ) : undefined;
    },
    // OPEN status displays as "Booked"
    // so when sortable, the rows look incorrect
    // "Booked" appears at the bottom when sorted Desc
    // because the sort term is actually "OPEN"
    enableSorting: false,
  }),
  columnHelper.accessor('qtyExpected', {
    header: 'Qty Expected',
    meta: { align: 'right' },
    cell: ({ getValue }) => {
      const value = getValue();
      return value !== undefined ? formatNumber(value) : undefined;
    },
  }),
  columnHelper.accessor('qtyReceived', {
    header: 'Qty Received',
    meta: { align: 'right' },
    cell: ({ getValue }) => {
      const value = getValue();
      return value !== undefined ? formatNumber(value) : undefined;
    },
  }),
  columnHelper.accessor('closedAt', {
    header: 'Date Closed',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatDate(value) : undefined;
    },
  }),
];

const presetViews: View[] = [
  {
    id: 'all',
    name: 'All',
    state: {
      filters: [],
    },
  },
  ...[AsnStatus.DRAFT, AsnStatus.OPEN, AsnStatus.CLOSED, AsnStatus.CANCELLED].map((status) => ({
    id: status,
    name: asnStatusTitle[status],
    state: {
      filters: [
        {
          id: 'status',
          operator: 'in',
          value: [status],
        },
      ],
    },
  })),
];

const tieBreakerSort = [
  {
    key: 'createdAt',
    direction: 'desc',
  },
  {
    key: 'name',
    direction: 'asc',
  },
] as const;

export function AsnList() {
  const { supplyGateway, accountsGateway } = useGateways();
  const [isLive, setIsLive] = useState(false);
  const [isPolling, triggerPolling] = usePulseFlag(DATA_POLLING_DURATION_MS);
  const onClickRow = useRowNavigationCallback<AsnListItem>('/supply/asns');

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'asns',
    presetViews,
  });

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      accountsGateway.getLocations(undefined, { signal }),
    select: ({ items }: { items: LocationDTO[] }) => items,
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      status: {
        label: 'Status',
        variant: 'multiselect',
        icon: FileTextIcon,
        options: [
          AsnStatus.DRAFT,
          AsnStatus.PENDING,
          AsnStatus.OPEN,
          AsnStatus.CLOSED,
          AsnStatus.CANCELLED,
        ],
        optionLabel: (option) => asnStatusTitle[option as AsnStatus] || option,
      },
      locationName: {
        label: 'Location',
        variant: 'multiselect',
        icon: MapPinIcon,
        options: locations?.map((loc: LocationDTO) => loc.name) ?? [],
        isLoading: isLoadingLocations,
      },
    },
    views,
    isLoadingSavedViews,
  });

  const filterMap = filters.getActiveFilterMap();

  const queryInput: QueryAsnsProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageSize * state.pagination.pageIndex,
    sortBy: [
      ...state.sorting.map((s) => ({
        key: s.id as SortField,
        direction: s.desc ? ('desc' as const) : ('asc' as const),
      })),
      ...tieBreakerSort,
    ],
    filters: {
      status: filterMap.status,
      locationName: filterMap.locationName,
    },
    search: filters.search,
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'asns',
    input: queryInput,
    query: (input, { signal }) => supplyGateway.queryAsns(input, { signal }),
    select: AsnListMapper.toData,
    refetchInterval: isLive || isPolling ? LIVE_DATA_INTERVAL_MS : undefined,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data: data?.data,
    rowCount: data?.total,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <ContainerIcon />
        </HeaderIcon>
        <HeaderTitle>ASNs</HeaderTitle>
        <HeaderActions>
          <DataImporter template={TemplateKey.ASNS} onComplete={triggerPolling} />
          <Button asChild>
            <Link to="/supply/asns/new">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} isLive={isLive} onChangeLive={setIsLive} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
